export type GlobalSearchMatch = {
  score: number;
  labelIndices: number[];
  breadcrumbIndices: number[][];
};

function isWordChar(char: string): boolean {
  return /[a-z0-9]/i.test(char);
}

function isWordStart(haystack: string, index: number): boolean {
  if (index <= 0) return true;
  return !isWordChar(haystack[index - 1] ?? "");
}

function tokenizeQuery(query: string): string[] {
  return query.trim().toLowerCase().split(/\s+/).filter(Boolean);
}

function findConsecutiveAtWordStart(haystackLower: string, token: string): number {
  let from = 0;
  while (from <= haystackLower.length - token.length) {
    const at = haystackLower.indexOf(token, from);
    if (at < 0) return -1;
    if (isWordStart(haystackLower, at)) return at;
    from = at + 1;
  }
  return -1;
}

function wordStartIndices(haystackLower: string): number[] {
  const starts: number[] = [];
  for (let i = 0; i < haystackLower.length; i++) {
    if (isWordStart(haystackLower, i) && isWordChar(haystackLower[i] ?? "")) {
      starts.push(i);
    }
  }
  return starts;
}

/**
 * Word-prefix / acronym match. The first character must land on a word start.
 * Later characters may extend that prefix consecutively, or jump to a later word
 * start. Greedy consecutive matching backtracks so space-less queries like
 * `peroverview` can skip an earlier prefix word (People) and hit Person + Overview.
 * Mid-word letters never start a hit.
 */
export function matchToken(haystackLower: string, token: string): { indices: number[]; score: number } | null {
  if (!token) return { indices: [], score: 0 };

  const consecutiveAt = findConsecutiveAtWordStart(haystackLower, token);
  if (consecutiveAt >= 0) {
    const indices = Array.from({ length: token.length }, (_, i) => consecutiveAt + i);
    return { indices, score: scoreIndices(haystackLower, indices) + 400 };
  }

  const wordStarts = wordStartIndices(haystackLower);
  const indices = matchFromWordStarts(token, haystackLower, wordStarts, 0, 0);
  if (!indices) return null;
  return { indices, score: scoreIndices(haystackLower, indices) };
}

function matchFromWordStarts(
  token: string,
  haystackLower: string,
  wordStarts: number[],
  queryIndex: number,
  minHaystackIndex: number
): number[] | null {
  if (queryIndex >= token.length) return [];

  const char = token[queryIndex]!;
  for (const start of wordStarts) {
    if (start < minHaystackIndex) continue;
    if (haystackLower[start] !== char) continue;
    const rest = matchAfterHit(token, haystackLower, wordStarts, queryIndex, start);
    if (rest) return [start, ...rest];
  }

  return null;
}

function matchAfterHit(
  token: string,
  haystackLower: string,
  wordStarts: number[],
  queryIndex: number,
  haystackIndex: number
): number[] | null {
  const nextQueryIndex = queryIndex + 1;
  if (nextQueryIndex >= token.length) return [];

  const nextChar = token[nextQueryIndex]!;
  const consecutiveIndex = haystackIndex + 1;

  if (consecutiveIndex < haystackLower.length && haystackLower[consecutiveIndex] === nextChar) {
    const rest = matchAfterHit(token, haystackLower, wordStarts, nextQueryIndex, consecutiveIndex);
    if (rest) return [consecutiveIndex, ...rest];
  }

  return matchFromWordStarts(token, haystackLower, wordStarts, nextQueryIndex, haystackIndex + 1);
}

function scoreIndices(haystackLower: string, indices: number[]): number {
  if (indices.length === 0) return 0;

  let score = indices.length * 10;
  for (let i = 0; i < indices.length; i++) {
    if (isWordStart(haystackLower, indices[i])) score += 80;
    if (i > 0 && indices[i] === indices[i - 1] + 1) score += 50;
  }

  score += Math.max(0, 50 - indices[0]);
  const span = indices[indices.length - 1] - indices[0] + 1;
  score += Math.max(0, 120 - span);
  return score;
}

function matchAllTokens(haystack: string, tokens: string[]): { indices: number[]; score: number } | null {
  const haystackLower = haystack.toLowerCase();
  const indexSet = new Set<number>();
  let score = 0;

  for (const token of tokens) {
    const matched = matchToken(haystackLower, token);
    if (!matched) return null;
    matched.indices.forEach((index) => indexSet.add(index));
    score += matched.score;
  }

  return { indices: [...indexSet].sort((a, b) => a - b), score };
}

type ScoringRegion = {
  kind: "breadcrumb" | "label" | "keyword";
  index: number;
  start: number;
  end: number;
};

function layoutScoringHaystack(
  label: string,
  breadcrumbs: string[],
  keywords: string[]
): { haystack: string; regions: ScoringRegion[] } {
  const raw = `${breadcrumbs.join(" ")} ${label} ${keywords.join(" ")}`;
  const haystack = raw.trim();
  const trimStart = raw.length - raw.trimStart().length;
  const regions: ScoringRegion[] = [];
  let offset = 0;

  breadcrumbs.forEach((text, index) => {
    if (index > 0) offset += 1;
    regions.push({ kind: "breadcrumb", index, start: offset, end: offset + text.length });
    offset += text.length;
  });

  offset += 1;
  regions.push({ kind: "label", index: 0, start: offset, end: offset + label.length });
  offset += label.length;

  keywords.forEach((text, index) => {
    offset += 1;
    regions.push({ kind: "keyword", index, start: offset, end: offset + text.length });
    offset += text.length;
  });

  return {
    haystack,
    regions: regions.map((region) => ({
      ...region,
      start: region.start - trimStart,
      end: region.end - trimStart
    }))
  };
}

function mapHaystackIndices(
  haystackIndices: number[],
  breadcrumbs: string[],
  regions: ScoringRegion[]
): { labelIndices: number[]; breadcrumbIndices: number[][] } {
  const labelIndices: number[] = [];
  const breadcrumbIndices = breadcrumbs.map(() => [] as number[]);

  for (const haystackIndex of haystackIndices) {
    const region = regions.find((candidate) => haystackIndex >= candidate.start && haystackIndex < candidate.end);
    if (!region) continue;
    const localIndex = haystackIndex - region.start;
    if (region.kind === "label") {
      labelIndices.push(localIndex);
    } else if (region.kind === "breadcrumb") {
      breadcrumbIndices[region.index]?.push(localIndex);
    }
  }

  return {
    labelIndices: uniqSorted(labelIndices),
    breadcrumbIndices: breadcrumbIndices.map(uniqSorted)
  };
}

function uniqSorted(indices: number[]): number[] {
  return [...new Set(indices)].sort((a, b) => a - b);
}

function unionIndices(left: number[], right: number[]): number[] {
  return uniqSorted([...left, ...right]);
}

export function matchGlobalSearchQuery(
  query: string,
  label: string,
  breadcrumbs: string[],
  keywords: string[] = []
): GlobalSearchMatch | null {
  const tokens = tokenizeQuery(query);
  if (tokens.length === 0) {
    return {
      score: 0,
      labelIndices: [],
      breadcrumbIndices: breadcrumbs.map(() => [])
    };
  }

  const { haystack, regions } = layoutScoringHaystack(label, breadcrumbs, keywords);
  const scored = matchAllTokens(haystack, tokens);
  if (!scored) return null;

  const labelMatch = matchAllTokens(label, tokens);
  let score = scored.score + (labelMatch ? 300 : 0);
  if (labelMatch && labelMatch.indices.length > 0) {
    score += Math.max(0, 80 - labelMatch.indices[0]);
  }
  const mapped = mapHaystackIndices(scored.indices, breadcrumbs, regions);

  return {
    score,
    labelIndices: unionIndices(collectFieldIndices(label, tokens), mapped.labelIndices),
    breadcrumbIndices: breadcrumbs.map((crumb, index) =>
      unionIndices(collectFieldIndices(crumb, tokens), mapped.breadcrumbIndices[index] ?? [])
    )
  };
}

const FACILITY_INITIAL_STOPWORDS = new Set(["the", "of", "at", "and", "a"]);
const ACRONYM_MIN_LENGTH = 3;
const ACRONYM_MAX_LENGTH = 6;
const ACRONYM_VOWELS = /[aeiouy]/;

function compactAcronymQuery(query: string): string | null {
  const stripped = query.trim();
  if (!stripped || /\s/.test(stripped)) return null;

  const compact = stripped.toLowerCase().replace(/[^a-z0-9]/g, "");
  if (compact.length < ACRONYM_MIN_LENGTH || compact.length > ACRONYM_MAX_LENGTH) return null;
  if (ACRONYM_VOWELS.test(compact)) return null;
  return compact;
}

function facilityInitialIndices(text: string, compact: string): number[] | null {
  const lower = text.toLowerCase();
  const starts: number[] = [];
  let index = 0;

  while (index < lower.length) {
    const char = lower[index] ?? "";
    if (!isWordChar(char)) {
      index += 1;
      continue;
    }

    const wordStart = index;
    index += 1;
    while (index < lower.length && isWordChar(lower[index] ?? "")) {
      index += 1;
    }

    const word = lower.slice(wordStart, index);
    if (!FACILITY_INITIAL_STOPWORDS.has(word)) {
      starts.push(wordStart);
    }
  }

  if (starts.length < compact.length) return null;

  const initials = starts.map((start) => lower[start] ?? "").join("");
  if (!initials.startsWith(compact)) return null;
  return starts.slice(0, compact.length);
}

export function matchDisplayNameIndices(text: string, query: string): number[] {
  const compact = compactAcronymQuery(query);
  if (compact) {
    const acronymIndices = facilityInitialIndices(text, compact);
    if (acronymIndices) return acronymIndices;
  }

  const tokens = tokenizeQuery(query);
  if (tokens.length === 0) return [];

  const lower = text.toLowerCase();
  const indices = new Set<number>();

  for (const token of tokens) {
    const matched = matchToken(lower, token);
    if (matched) {
      matched.indices.forEach((index) => indices.add(index));
      continue;
    }

    const start = lower.indexOf(token);
    if (start < 0) continue;
    for (let offset = 0; offset < token.length; offset++) {
      indices.add(start + offset);
    }
  }

  return [...indices].sort((a, b) => a - b);
}

function collectFieldIndices(text: string, tokens: string[]): number[] {
  const lower = text.toLowerCase();
  const indices = new Set<number>();
  for (const token of tokens) {
    const matched = matchToken(lower, token);
    matched?.indices.forEach((index) => indices.add(index));
  }
  return [...indices].sort((a, b) => a - b);
}
